'use client';

import { useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import Icon from '@/components/ui/AppIcon';

const UserBehavior = () => {
  const sessionDurationData = useMemo(() => [
    { duration: '0-1 min', users: 234 },
    { duration: '1-3 min', users: 567 },
    { duration: '3-5 min', users: 892 },
    { duration: '5-10 min', users: 456 },
    { duration: '10+ min', users: 189 },
  ], []);

  const deviceData = useMemo(() => [
    { name: 'Mobile', value: 52, color: '#3B82F6' },
    { name: 'Desktop', value: 38, color: '#8B5CF6' },
    { name: 'Tablet', value: 10, color: '#F59E0B' },
  ], []);

  const topPagesData = useMemo(() => [
    { page: 'Flight Search', views: 3456, avgTime: '3:24' },
    { page: 'Homepage', views: 2891, avgTime: '1:45' },
    { page: 'Hotels', views: 1678, avgTime: '2:56' },
    { page: 'Holidays', views: 1234, avgTime: '3:12' },
    { page: 'Booking Management', views: 987, avgTime: '4:18' },
  ], []);

  const stats = [
    { label: 'Active Users', value: '1,847', change: '+8.3%', icon: 'UserGroupIcon', trend: 'up' },
    { label: 'Avg. Session Duration', value: '4m 32s', change: '+18s', icon: 'ClockIcon', trend: 'up' },
    { label: 'Pages per Session', value: '3.8', change: '+0.4', icon: 'DocumentTextIcon', trend: 'up' },
    { label: 'Bounce Rate', value: '32.4%', change: '-2.1%', icon: 'ArrowUturnLeftIcon', trend: 'up' },
  ];

  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat, index) => (
          <div key={index} className="bg-card rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-2">
              <Icon name={stat.icon as any} size={24} className="text-primary" />
              <span className={`text-sm font-medium ${
                stat.trend === 'up' ? 'text-success' : 'text-error'
              }`}>
                {stat.change}
              </span>
            </div>
            <div className="text-2xl font-bold text-foreground mb-1">{stat.value}</div>
            <div className="text-sm text-muted-foreground">{stat.label}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Session Duration */}
        <div className="bg-card rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-bold text-foreground mb-4">Session Duration Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={sessionDurationData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="duration" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="users" fill="#3B82F6" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Device Distribution */}
        <div className="bg-card rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-bold text-foreground mb-4">Device Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={deviceData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {deviceData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Top Pages */}
      <div className="bg-card rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-bold text-foreground mb-4">Top Pages by Views</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Page</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Views</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Avg. Time</th>
              </tr>
            </thead>
            <tbody>
              {topPagesData.map((page, index) => (
                <tr key={index} className="border-b border-border last:border-0">
                  <td className="py-3 px-4 text-sm font-medium text-foreground">{page.page}</td>
                  <td className="py-3 px-4 text-sm text-right text-foreground">{page.views.toLocaleString()}</td>
                  <td className="py-3 px-4 text-sm text-right text-muted-foreground">{page.avgTime}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default UserBehavior;